<template>
    <div class="voting-widget">
        <div class="voting-header">
            <p class="widget-question">{{ voting.question }}</p>
        </div>

        <div v-if="!hasVoted" class="voting-options">
            <button
                v-for="answer in sortedAnswers"
                :key="answer.id"
                @click="submitVote(answer.id)"
                :disabled="isSubmitting"
                class="voting-option"
                :class="{ 'selected': selectedAnswer === answer.id }"
            >
                <span class="option-text">{{ answer.text }}</span>
            </button>
        </div>

        <div v-else class="voting-results">
            <div class="vote-confirmation">
                <p class="confirmation-message">✓ {{ t('voting.thankYouVote') }}</p>
                <p class="selected-answer">{{ t('voting.yourAnswer') }} {{ selectedAnswerText }}</p>
            </div>

            <!-- Show results only when both result_app is true AND voting is shared -->
            <div v-if="voting.result_app && voting.shared" class="results-display">
                <h4 class="results-title">{{ t('voting.results') }}</h4>
                <div v-for="answer in sortedAnswers" :key="answer.id" class="result-item">
                    <div class="result-header">
                        <span class="answer-text">{{ answer.text }}</span>
                        <span v-if="voting.vote_info" class="vote-count">{{ answer.votes }} votes ({{ getPercentage(answer.votes) }}%)</span>
                        <span v-else class="vote-percentage">{{ getPercentage(answer.votes) }}%</span>
                    </div>
                    <div class="progress">
                        <div 
                            class="progress-bar bg-primary"
                            :style="{ width: getPercentage(answer.votes) + '%' }"
                        ></div>
                    </div>
                </div>
                <div v-if="voting.vote_info" class="total-votes">
                    {{ t('voting.totalVotes') }} {{ totalVotes }}
                </div>
            </div>

            <!-- Message when results are not shared yet -->
            <div v-else-if="voting.result_app && !voting.shared" class="status-message">
                <svg class="status-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <p class="status-title">{{ t('voting.voteRecorded') }}</p>
                <p class="status-subtitle">{{ t('voting.resultsWillBeRevealed') }}</p>
            </div>
        </div>

        <div v-if="error" class="alert alert-danger">
            {{ error }}
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useApi } from '@/composables/useApi.js'
import { useWebSocket } from '@/composables/useWebSocket.js'
import { useAudienceConfig } from '@/composables/useAudienceConfig.js'
import { validateConfig } from '@/utils/config.js'

const { t } = useI18n()

const props = defineProps({
    sessionId: {
        type: String,
        required: true
    },
    // External configuration props
    apiBaseUrl: {
        type: String,
        default: ''
    },
    pusherConfig: {
        type: Object,
        default: () => ({})
    },
    authToken: {
        type: String,
        default: null
    },
    congressId: {
        type: [String, Number],
        default: null
    }
})

const emit = defineEmits(['vote-submitted', 'session-stopped', 'session-started'])

// Configuration setup - use the new composable for consistent config
const config = useAudienceConfig(props)

// Validate configuration
const configValidation = computed(() => validateConfig(config.value))

// Setup API client
const { api } = useApi(config.value)

// Setup WebSocket
const { createEcho, subscribeToChannel } = useWebSocket(config.value)

// Reactive state
const votingItem = ref({})
const hasVoted = ref(false)
const selectedAnswer = ref(null)
const selectedAnswerText = ref('')
const isSubmitting = ref(false)
const error = ref('')
const voteResults = ref([])

// Real-time updates
let channel = null
let echo = null

// Generate unique user ID for this audience window (for testing purposes)
// In production, this would come from authentication
const generateUserId = () => {
    // Generate a unique user ID per browser window/tab to simulate different users
    // Use a combination of sessionStorage (unique per tab) and a generated ID
    const storageKey = 'audience_user_id'
    let userId = sessionStorage.getItem(storageKey)
    
    if (!userId) {
        // Generate a new UUID for this browser tab/window
        userId = 'audience_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now().toString(36)
        sessionStorage.setItem(storageKey, userId)
        console.log('VotingWidget: Generated new user ID for this window:', userId)
    } else {
        console.log('VotingWidget: Using existing user ID for this window:', userId)
    }
    
    return userId
}

const userId = generateUserId()

// Computed
const sortedAnswers = computed(() => {
    if (!votingItem.value || !votingItem.value.answers) return []
    
    if (voteResults.value.length > 0) {
        // For results display, keep original order (not sorted by votes)
        return [...voteResults.value].sort((a, b) => (a.order || 0) - (b.order || 0))
    }
    // For voting options, use original order
    return [...votingItem.value.answers].sort((a, b) => (a.order || 0) - (b.order || 0))
})

const totalVotes = computed(() => {
    return sortedAnswers.value.reduce((sum, answer) => sum + answer.votes, 0)
})

const voting = computed(() => votingItem.value)

// Methods
const loadVotingItem = async () => {
    try {
        console.log("🔍 Loading Voting item with API baseURL:", config.value.apiBaseUrl)
        const response = await api.get(`/api/sessions/${props.sessionId}/votings`)
        
        // The API returns an array, so we need to extract the first item
        const votingData = response.data || []
        votingItem.value = Array.isArray(votingData) && votingData.length > 0 ? votingData[0] : {}
        
        console.log("✅ Voting item loaded successfully:", votingItem.value)
        console.log("🔍 Voting item ID:", votingItem.value.id)
        console.log("🔍 Voting item active status:", votingItem.value.active)
        
        // Once votingItem is loaded, check voting status
        if (votingItem.value && votingItem.value.id) {
            console.log("🚀 Voting item has ID, checking status...")
            await checkVotingStatus()
            
            // Check if the voting session is already active and emit event to show widget
            if (votingItem.value.active) {
                console.log("✅ Voting session is already active, emitting session-started event")
                emit('session-started', { voting: votingItem.value })
            } else {
                console.log("ℹ️ Voting session exists but is not active yet")
            }
        } else {
            console.warn("⚠️ Voting item loaded but no ID found:", votingItem.value)
        }
        
    } catch (err) {
        console.error('❌ Error loading Voting item:', err)
        console.error('📡 API config being used:', { 
            baseURL: config.value.apiBaseUrl,
            fullURL: `${config.value.apiBaseUrl}/api/sessions/${props.sessionId}/votings`
        })
    }
}

const submitVote = async (answerId) => {
    if (isSubmitting.value || hasVoted.value) return

    isSubmitting.value = true
    error.value = ''
    selectedAnswer.value = answerId

    const answer = votingItem.value.answers.find(a => a.id === answerId)
    selectedAnswerText.value = answer ? answer.text : ''

    try {
        const response = await api.post(`/api/audience/voting/${votingItem.value.id}/vote`, {
            answer_id: answerId,
            user_id: userId
        })

        hasVoted.value = true
        
        // Don't update local results - let them come through the broadcast
        // This ensures all clients see the same vote counts at the same time

        emit('vote-submitted', {
            voting_id: votingItem.value.id,
            answer_id: answerId,
            answer_text: selectedAnswerText.value
        })

    } catch (err) {
        console.error('Error submitting vote:', err)
        
        if (err.response?.status === 409) {
            error.value = 'You have already voted in this poll.'
            hasVoted.value = true
        } else if (err.response?.status === 404) {
            error.value = 'This poll is no longer active.'
        } else {
            error.value = 'Failed to submit vote. Please try again.'
        }
        
        selectedAnswer.value = null
        selectedAnswerText.value = ''
    } finally {
        isSubmitting.value = false
    }
}

const getPercentage = (votes) => {
    if (totalVotes.value === 0) return 0
    return Math.round((votes / totalVotes.value) * 100)
}

// Check if user has already voted when component loads
const checkVotingStatus = async () => {
    if (!votingItem.value || !votingItem.value.id) return
    
    try {
        const response = await api.get(`/api/audience/voting/${votingItem.value.id}/status`, {
            params: { user_id: userId }
        })
        if (response.data.has_voted) {
            hasVoted.value = true
            selectedAnswerText.value = response.data.voted_answer || ''
            console.log('VotingWidget: User has already voted for:', selectedAnswerText.value)
        }
        if (response.data.results) {
            voteResults.value = response.data.results
        }
    } catch (err) {
        console.error('Error checking voting status:', err)
    }
}

// Initialize real-time updates using useWebSocket composable
const initializePusher = () => {
    console.log("🚀 INITIALIZE VOTING PUSHER - START")
    console.log("📋 Current config:", config.value)
    
    try {
        // Use the imported composable
        console.log("📡 Creating Echo instance with config:", config.value)
        echo = createEcho()
        
        if (!echo) {
            console.warn('❌ Failed to create Echo instance, real-time updates disabled')
            return
        }
        
        console.log("✅ Echo instance created successfully")

        // Subscribe to the session channel
        let finalCongressId = props.congressId

        finalCongressId = parseInt(finalCongressId)
        if (isNaN(finalCongressId)) {
            console.error('VotingWidget: Invalid congressId format, using fallback 1657')
            finalCongressId = 1657
        }
        
        const channelName = `session.${finalCongressId}.${props.sessionId}`
        console.log('Voting Widget subscribing to channel:', channelName)
        console.log('Voting Widget ✅ Using congress_id:', finalCongressId)
        
        // Use the subscribeToChannel composable
        channel = subscribeToChannel(echo, channelName, {
            'voting.started': (data) => {
                console.log('Voting session started:', data)
                error.value = ''
                if (data.voting && data.voting.active) {
                    console.log("Voting session started, emitting event to parent")
                    console.log(data)
                    votingItem.value = data.voting
                    // Emit event to parent component
                    emit('session-started', { voting: data.voting })
                }
            },

                'voting.updated': (data) => {
                console.log('Voting session updated:', data)
                if (data.voting) {
                    // Update the voting item with new data including result_app flag
                    votingItem.value = data.voting
                    console.log('Updated voting item - result_app:', votingItem.value.result_app)
                    console.log('Updated voting item - shared:', votingItem.value.shared)
                    
                    // If results should be shown, update them with the latest vote counts
                    if (data.voting.answers) {
                        voteResults.value = data.voting.answers
                        console.log('Updated vote results:', voteResults.value)
                    }
                    
                    // If result_app is now true, fetch the latest results from API
                    if (data.voting.result_app && hasVoted.value) {
                        console.log('result_app is now true, fetching latest results...')
                        checkVotingStatus()
                    }
                }
            },
            
            
            'voting.stopped': (data) => {
                console.log('Voting session stopped:', data)
                error.value = t('widgets.voting.sessionEnded')
                // Emit event to parent component to hide the widget
                emit('session-stopped', { voting: data.voting })
            },
            
            'voting.shared': (data) => {
                console.log('Voting results shared:', data)
                if (votingItem.value && votingItem.value.id === data.voting_id) {
                    votingItem.value.shared = true
                    if (data.results) {
                        voteResults.value = data.results
                    }
                }
            },
            
            'voting.vote.submitted': (data) => {
                console.log('Vote submitted by another user:', data)
                if (data.results) {
                    voteResults.value = data.results
                }
            }
        })
        
        console.log("✅ VotingWidget: WebSocket channel setup complete")
    } catch (error) {
        console.error('Error initializing WebSocket for Voting Widget:', error)
    }
}

const cleanupPusher = () => {
    if (channel) {
        try {
            channel.stopListening()
        } catch (error) {
            console.log('Channel cleanup handled by composable')
        }
    }
    // The useWebSocket composable handles Echo cleanup internally
    // Don't disconnect Echo if it's the global instance
    if (echo && echo !== window.Echo) {
        echo.disconnect()
    }
}

// Lifecycle
onMounted(() => {
    if (props.sessionId) {
        console.log('Voting Widget mounted, loading Voting item...')
        console.log('Session ID:', props.sessionId)
       
        loadVotingItem()
        initializePusher()
    } else {
        console.warn('Voting Widget mounted without valid sessionId, skipping initialization.')
    }
})

onUnmounted(() => {
    cleanupPusher()
})
</script>

<style scoped>
/* Voting Widget Bootstrap Styling */
.voting-widget {
    background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
    padding: 1.5rem;
    margin-bottom: 1rem;
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.15);
    animation: slideIn 0.3s ease-out;
}

.voting-header {
    margin-bottom: 1.5rem;
    text-align: center;
}

.widget-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1976d2;
    margin-bottom: 0.5rem;
}

.widget-question {
    color: #424242;
    margin-bottom: 0;
    font-size: 1.1rem;
    font-weight: 500;
}

/* Voting Options */
.voting-options {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.voting-option {
    width: 100%;
    text-align: left;
    padding: 1rem;
    border: 2px solid #90caf9;
    border-radius: 0.5rem;
    background: #ffffff;
    color: #424242;
    font-weight: 500;
    transition: all 0.2s ease;
    cursor: pointer;
}

.voting-option:hover {
    background: #e3f2fd;
    border-color: #2196f3;
    transform: translateY(-1px);
}

.voting-option:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

.voting-option.selected {
    background: #e3f2fd;
    border-color: #1976d2;
    box-shadow: 0 2px 8px rgba(33, 150, 243, 0.2);
}

.option-text {
    font-weight: 500;
}

/* Vote Confirmation */
.vote-confirmation {
    margin-bottom: 1.5rem;
    text-align: center;
    padding: 1rem;
    background: linear-gradient(135deg, #e8f5e8 0%, #f3e5f5 100%);
    border-radius: 0.5rem;
    border: 1px solid #4caf50;
}

.confirmation-message {
    color: #2e7d32;
    font-weight: 600;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
}

.selected-answer {
    color: #616161;
    font-size: 0.875rem;
    margin: 0;
}

/* Results Display */
.results-display {
    margin-top: 1rem;
}

.results-title {
    font-weight: 600;
    color: #424242;
    margin-bottom: 1rem;
    font-size: 1.1rem;
}

.result-item {
    margin-bottom: 1rem;
    animation: fadeIn 0.5s ease-out;
}

.result-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.answer-text {
    font-size: 0.875rem;
    font-weight: 500;
    color: #424242;
    flex: 1;
}

.vote-count,
.vote-percentage {
    font-size: 0.875rem;
    color: #616161;
    font-weight: 500;
}

.progress {
    width: 100%;
    height: 0.75rem;
    background-color: #e0e0e0;
    border-radius: 0.375rem;
    overflow: hidden;
}

.progress-bar {
    height: 100%;
    background: linear-gradient(90deg, #2196f3 0%, #1976d2 100%);
    border-radius: 0.375rem;
    transition: width 0.5s ease;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.1);
}

.total-votes {
    text-align: center;
    margin-top: 1rem;
    font-size: 0.875rem;
    color: #616161;
    padding: 0.5rem;
    background: #f5f5f5;
    border-radius: 0.25rem;
}

/* Status Messages */
.status-message {
    text-align: center;
    padding: 2rem;
    color: #616161;
}

.status-icon {
    width: 3rem;
    height: 3rem;
    margin: 0 auto 1rem;
    color: #616161;
}

.status-title {
    font-weight: 500;
    margin-bottom: 0.5rem;
    color: #424242;
}

.status-subtitle {
    font-size: 0.875rem;
    margin: 0;
}

/* Animations */
@keyframes slideIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes fadeIn {
    from { 
        opacity: 0;
        transform: scale(0.95);
    }
    to { 
        opacity: 1;
        transform: scale(1);
    }
}

/* Responsive Design */
@media (max-width: 768px) {
    .voting-widget {
        padding: 1rem;
    }
    
    .result-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 0.25rem;
    }
    
    .vote-confirmation {
        padding: 0.75rem;
    }
    
    .voting-option {
        padding: 0.75rem;
    }
}
</style>